import { InsertEmoticonOutlined } from '@mui/icons-material';
import { which } from 'node-emoji';

import { Popover } from '@mui/material';
import { EmojiStyle } from 'emoji-picker-react';
import { use, useState } from 'react';

import { Or2Button } from '@/components/button/Or2Button';

import { useMutation } from '@/hooks/mutationWithSnackbar';

import { graphql, type DocumentType } from '@/gql';

import { extractConnectionProps } from '@/utils/queries/queryExtracts';

import {
  convertEmojiToMarkdown,
  getSkinToneFromEmoji,
} from '@/utils/strings/emoji';

import { EmojiPickerContext } from '@/modules/editor/contexts/EmojiPickerContext';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import {
  ADD_REACTION_MUTATION,
  REMOVE_REACTION_MUTATION,
} from './reaction-bar/PostReaction';

const POST_REACTION_BUTTON_POST_FRAGMENT = graphql(`
  fragment PostReactionButtonPostFragment on Post {
    _id
    reactionsConnection {
      edges {
        node {
          _id
          reaction
          skinTone
          userContext
          time
          user {
            ...authorData
          }
        }
      }
    }
  }
`);

interface Props {
  post: DocumentType<typeof POST_REACTION_BUTTON_POST_FRAGMENT>;
}

export const PostReactionButton = ({ post }: Props) => {
  const { _id: postId, reactionsConnection } = post;

  const reactions = extractConnectionProps(reactionsConnection);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { currentUserId } = use(CurrentUserContext);
  const EmojiPicker = use(EmojiPickerContext);

  const [addReactionToPost, { loading: addLoading }] = useMutation(
    ADD_REACTION_MUTATION,
    {
      completionMessage: 'Reaction added',
    },
  );

  const [removeReactionFromPost, { loading: removeLoading }] = useMutation(
    REMOVE_REACTION_MUTATION,
    {
      completionMessage: 'Reaction removed',
    },
  );

  const userReactions = reactions.filter(
    (reaction) => reaction.user?._id === currentUserId,
  );

  return (
    <>
      <Or2Button
        color="secondary"
        size="small"
        disabled={addLoading || removeLoading}
        startIcon={<InsertEmoticonOutlined />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        React
      </Or2Button>
      <Popover
        id={anchorEl ? 'emoji-popover' : undefined}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        <EmojiPicker
          emojiStyle={EmojiStyle.NATIVE}
          onEmojiClick={async ({ emoji }) => {
            setAnchorEl(null);

            const emojiMarkdown = which(emoji, { markdown: true });
            const markdown = convertEmojiToMarkdown(emoji);
            const skinTone = getSkinToneFromEmoji(emoji);

            if (!markdown) {
              return;
            }

            const reactionId = userReactions.find(
              (reaction) => reaction.reaction === emojiMarkdown,
            )?._id;

            const isSameReactionWithSkinTone =
              Boolean(reactionId) &&
              userReactions.find(
                (reaction) => reaction.skinTone === skinTone?.markdown,
              );

            if (isSameReactionWithSkinTone) {
              return;
            }

            if (reactionId) {
              await removeReactionFromPost({
                variables: {
                  data: {
                    post: postId,
                    reaction: reactionId,
                  },
                },
              });
            }

            addReactionToPost({
              variables: {
                data: {
                  post: postId,
                  reaction: markdown,
                },
              },
            });
          }}
        />
      </Popover>
    </>
  );
};
